import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ClipboardList, Clock, CheckCircle, AlertTriangle, Building2, User, Calendar, ArrowLeft, Printer, Trash2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { LayoutWrapper } from '@/components/LayoutWrapper';
import { format } from 'date-fns';
import EmailChecklistButton from '@/components/EmailChecklistButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Checklist {
  id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  scheduled_for: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  template_id: string;
  unit_id: string;
  assigned_to: string | null;
  checklist_templates: {
    name: string;
    frequency: string;
  };
  units: {
    name: string;
  };
  users_public?: {
    name: string;
  };
}

const Checklists = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'in_progress' | 'completed'>('all');
  const [unitInfo, setUnitInfo] = useState<{ id: string; name: string } | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  
  const unitId = searchParams.get('unit');

  useEffect(() => {
    fetchChecklists();
  }, [user]);

  const fetchChecklists = async () => {
    if (!user) return;

    try {
      let query = supabase
        .from('checklists')
        .select(`
          *,
          checklist_templates!inner(name, frequency),
          units!inner(name),
          users_public(name)
        `)
        .order('scheduled_for', { ascending: false });
      
      // Filter by unit if unitId is provided
      if (unitId) {
        query = query.eq('unit_id', unitId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setChecklists(data || []);

      // If filtering by unit, fetch unit info for the header
      if (unitId && data && data.length > 0) {
        setUnitInfo({
          id: unitId,
          name: data[0].units.name
        });
      } else if (unitId) {
        // If unitId is provided but no checklists found, fetch unit info separately
        const { data: unitData, error: unitError } = await supabase
          .from('units')
          .select('id, name')
          .eq('id', unitId)
          .single();
        
        if (!unitError && unitData) {
          setUnitInfo(unitData);
        }
      } else {
        setUnitInfo(null);
      }
    } catch (error: any) {
      toast({
        title: "Error loading checklists",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChecklist = async (checklistId: string, checklistName: string) => {
    setDeletingIds(prev => new Set(prev).add(checklistId));
    
    try {
      const { error } = await supabase
        .from('checklists')
        .delete()
        .eq('id', checklistId);

      if (error) throw error;

      // Remove the checklist from the local state
      setChecklists(prev => prev.filter(c => c.id !== checklistId));
      
      toast({
        title: "Checklist deleted",
        description: `"${checklistName}" has been deleted successfully.`,
      });
    } catch (error: any) {
      toast({
        title: "Error deleting checklist",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(checklistId);
        return newSet;
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'overdue':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <ClipboardList className="h-4 w-4 text-orange-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'in_progress':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'overdue':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      default:
        return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
    }
  };

  const filteredChecklists = checklists.filter(checklist => 
    filter === 'all' || checklist.status === filter
  );

  const stats = {
    total: checklists.length,
    pending: checklists.filter(c => c.status === 'pending').length,
    in_progress: checklists.filter(c => c.status === 'in_progress').length,
    completed: checklists.filter(c => c.status === 'completed').length,
    overdue: checklists.filter(c => c.status === 'overdue').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-accent/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading checklists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent/20">
      {/* Header */}
      <header className="glass-nav">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <Link 
              to={unitId ? "/units" : "/dashboard"} 
              className="text-sm text-muted-foreground hover:text-crystal-white flex items-center gap-1"
            >
              <ArrowLeft className="h-3 w-3" />
              {unitId ? "Back to Units" : "Back to Dashboard"}
            </Link>
            <h1 className="text-2xl font-bold text-crystal-white">
              {unitId && unitInfo ? `${unitInfo.name} - Checklists` : 'Checklists'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {unitId && unitInfo 
                ? `Track and manage checklists for ${unitInfo.name}`
                : 'Track and manage your food safety checklists'
              }
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/checklists/new">
              <Button className="cta-primary">
                <ClipboardList className="h-4 w-4 mr-2" />
                Create New Checklist
              </Button>
            </Link>
            <Link to="/templates">
              <Button variant="outline" className="cta-secondary">
                Manage Templates
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="glass-panel cursor-pointer" onClick={() => setFilter('all')}>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-crystal-white">{stats.total}</div>
              <div className="text-xs text-muted-foreground">Total</div>
            </CardContent>
          </Card>
          <Card className="glass-panel cursor-pointer" onClick={() => setFilter('pending')}>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-400">{stats.pending}</div>
              <div className="text-xs text-muted-foreground">Pending</div>
            </CardContent>
          </Card>
          <Card className="glass-panel cursor-pointer" onClick={() => setFilter('in_progress')}>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-400">{stats.in_progress}</div>
              <div className="text-xs text-muted-foreground">In Progress</div>
            </CardContent>
          </Card>
          <Card className="glass-panel cursor-pointer" onClick={() => setFilter('completed')}>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-400">{stats.completed}</div>
              <div className="text-xs text-muted-foreground">Completed</div>
            </CardContent>
          </Card>
          <Card className="glass-panel cursor-pointer" onClick={() => setFilter('all')}>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-400">{stats.overdue}</div>
              <div className="text-xs text-muted-foreground">Overdue</div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {(['all', 'pending', 'in_progress', 'completed'] as const).map((status) => (
            <Button
              key={status}
              variant={filter === status ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(status)}
              className={filter === status ? "cta-primary" : "cta-secondary"}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
            </Button>
          ))}
        </div>

        {/* Checklists List */}
        {filteredChecklists.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-crystal-white mb-2">No checklists found</h2>
            <p className="text-muted-foreground mb-6">
              {filter === 'all' 
                ? "Create checklist templates to start scheduling food safety checks."
                : `No ${filter.replace('_', ' ')} checklists at the moment.`
              }
            </p>
            <Link to="/templates">
              <Button className="cta-primary">
                <ClipboardList className="h-4 w-4 mr-2" />
                Manage Templates
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredChecklists.map((checklist) => (
              <Card key={checklist.id} className="glass-panel hover:glass-panel-elevated transition-all duration-200">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-primary/10">
                        {getStatusIcon(checklist.status)}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{checklist.checklist_templates.name}</CardTitle>
                        <CardDescription className="flex items-center gap-4 mt-2">
                          <div className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {checklist.units.name}
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(checklist.scheduled_for), 'MMM d, yyyy')}
                          </div>
                          {checklist.users_public && (
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {checklist.users_public.name}
                            </div>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                     <div className="flex items-center gap-3">
                       <Badge className={`${getStatusColor(checklist.status)} capitalize`}>
                         {checklist.status.replace('_', ' ')}
                       </Badge>
                       <div className="flex gap-2">
                         {checklist.status === 'completed' && (
                           <>
                             <Link to={`/checklists/${checklist.id}/print`}>
                               <Button size="sm" variant="outline" className="cta-secondary">
                                 <Printer className="h-3 w-3 mr-1" />
                                 Print Report
                               </Button>
                             </Link>
                             <EmailChecklistButton checklistId={checklist.id} />
                           </>
                         )}
                         <Link to={`/checklists/${checklist.id}/execute`}>
                           <Button size="sm" className="cta-primary">
                             {checklist.status === 'completed' ? 'View Details' : 'Execute'}
                           </Button>
                         </Link>
                         <AlertDialog>
                           <AlertDialogTrigger asChild>
                             <Button 
                               size="sm" 
                               variant="outline" 
                               className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                               disabled={deletingIds.has(checklist.id)}
                             >
                               {deletingIds.has(checklist.id) ? (
                                 <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-1"></div>
                               ) : (
                                 <Trash2 className="h-3 w-3 mr-1" />
                               )}
                               Delete
                             </Button>
                           </AlertDialogTrigger>
                           <AlertDialogContent>
                             <AlertDialogHeader>
                               <AlertDialogTitle>Delete Checklist</AlertDialogTitle>
                               <AlertDialogDescription>
                                 Are you sure you want to delete "{checklist.checklist_templates.name}"? 
                                 This action cannot be undone. All associated data and entries will be permanently removed.
                               </AlertDialogDescription>
                             </AlertDialogHeader>
                             <AlertDialogFooter>
                               <AlertDialogCancel>Cancel</AlertDialogCancel>
                               <AlertDialogAction
                                 onClick={() => handleDeleteChecklist(checklist.id, checklist.checklist_templates.name)}
                                 className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                               >
                                 Delete Checklist
                               </AlertDialogAction>
                             </AlertDialogFooter>
                           </AlertDialogContent>
                         </AlertDialog>
                       </div>
                     </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-4">
                      <span>Frequency: {checklist.checklist_templates.frequency}</span>
                      {checklist.started_at && (
                        <span>Started: {format(new Date(checklist.started_at), 'MMM d, h:mm a')}</span>
                      )}
                      {checklist.completed_at && (
                        <span>Completed: {format(new Date(checklist.completed_at), 'MMM d, h:mm a')}</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Checklists;